/* eslint-disable react/jsx-no-literals -- inline SVG icons + static card copy */
/**
 * PhaseTwoStub — sequenced-deliverable card.
 * Renders a catalog capability with its live/roadmap status (from StubAtom.status,
 * surfaced via formatStubStatus). Renders inline within a tab/route; communicates
 * "we know this requirement, we've sequenced it."
 * Shared between web and mobile via platform-specific wrappers.
 */
import type { StubAtom } from "./stub-catalog";
import { formatStubStatus } from "./format-stub-status";

export interface PhaseTwoStubProps extends StubAtom {
  /** Render variant — web uses className-based Tailwind; mobile wraps in RN views */
  variant?: "web" | "mobile";
  /**
   * Optional translator (D1/W5). Supplied by trilingual web consumers so the
   * catalog-keyed copy (name/description/why/liveLink) and this card's own chrome
   * resolve via `t()`. Omitted → the English StubAtom fields + literals below are
   * the SSoT (mobile has its own native card, so this only ever renders on web,
   * but the fallback keeps it correct when a caller has not migrated yet).
   */
  t?: (key: string, params?: Record<string, string | number>) => string;
}

/**
 * Web-only PhaseTwoStub card. Mobile has its own native implementation
 * that mirrors this structure using React Native primitives.
 */
export function PhaseTwoStub({
  rId,
  name,
  description,
  status,
  sequencingReason,
  liveLink,
  nameKey,
  descriptionKey,
  sequencingReasonKey,
  t,
}: PhaseTwoStubProps) {
  const displayName = t && nameKey ? t(nameKey) : name;
  const displayDescription = t && descriptionKey ? t(descriptionKey) : description;
  const displayReason =
    t && sequencingReasonKey ? t(sequencingReasonKey) : sequencingReason;
  const displayLinkLabel =
    t && liveLink?.labelKey ? t(liveLink.labelKey) : liveLink?.label;
  const whyLabel = t
    ? status.kind === "live"
      ? t("stubs.whyNow")
      : t("stubs.whyLater")
    : status.kind === "live"
      ? "Why now:"
      : "Why later:";
  const notifyLabel = t ? t("stubs.notifyMe") : "Notify me when ready";
  return (
    <article
      className="rounded-xl border border-neutral-100 bg-white p-6 shadow-sm hover:shadow-card-hover transition-shadow duration-base"
      data-rid={rId}
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <h3 className="text-lg font-semibold text-neutral-900">{displayName}</h3>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-secondary/10 px-3 py-1 text-xs font-medium text-brand-secondary whitespace-nowrap">
          <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {formatStubStatus(status, t).label}
        </span>
      </header>

      <p className="mb-4 text-sm text-neutral-700 leading-relaxed">
        {displayDescription}
      </p>

      <div className="rounded-lg bg-neutral-50 p-3 text-xs text-neutral-600">
        <strong className="text-neutral-700">{whyLabel}</strong>{" "}
        {displayReason}
      </div>

      {liveLink && (
        <a
          href={liveLink.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-1 text-sm text-brand-primary-text hover:underline"
        >
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
          {displayLinkLabel}
        </a>
      )}

      <button
        type="button"
        onClick={() => {
          alert(
            t
              ? t("stubs.notifyAlert", { name: displayName })
              : `We'll notify you when ${displayName} is available.`,
          );
        }}
        className="mt-4 inline-flex w-full items-center justify-center rounded-lg border border-brand-primary px-4 py-2.5 text-sm font-medium text-brand-primary-text hover:bg-brand-primary-hover hover:text-white transition-colors duration-base"
      >
        {notifyLabel}
      </button>
    </article>
  );
}
